import type { ExecuteWorkflowNodeRequest } from '@unifyapps/network/generated/models/executeWorkflowNodeRequest';
import { executeWorkflowNode } from '@unifyapps/network/generated/workflow-rest-api/workflow-rest-api';
import { useGetMutationMeta } from '@unifyapps/network/hooks/useGetMutationMeta';
import type {
  MutationFunction,
  UseMutationOptions,
  UseMutationResult,
} from '@unifyapps/network/react-query';
import { useMutation } from '@unifyapps/network/react-query';
import type { ErrorType, executeFetch } from '@unifyapps/network/utils/fetch';

type SecondParameter<T extends (...args: unknown[]) => unknown> = Parameters<T>[1];

export const getExecuteWorkflowNodeMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof executeWorkflowNode>>,
    TError,
    { data: ExecuteWorkflowNodeRequest },
    TContext
  >;
  request?: SecondParameter<typeof executeFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof executeWorkflowNode>>,
  TError,
  { data: ExecuteWorkflowNodeRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof executeWorkflowNode>>,
    { data: ExecuteWorkflowNodeRequest }
  > = ({ data }) => {
    return executeWorkflowNode(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ExecuteWorkflowNodeMutationResult = NonNullable<
  Awaited<ReturnType<typeof executeWorkflowNode>>
>;
export type ExecuteWorkflowNodeMutationBody = ExecuteWorkflowNodeRequest;
export type ExecuteWorkflowNodeMutationError = ErrorType<unknown>;

export const useExecuteWorkflowNodeMutation = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof executeWorkflowNode>>,
    TError,
    { data: ExecuteWorkflowNodeRequest },
    TContext
  >;
  request?: SecondParameter<typeof executeFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof executeWorkflowNode>>,
  TError,
  { data: ExecuteWorkflowNodeRequest },
  TContext
> => {
  const meta = useGetMutationMeta();

  const optionsWithMeta = {
    ...options,
    request: {
      ...options?.request,
      meta: {
        ...meta,
        ...options?.request?.meta,
      },
    },
  };

  const mutationOptions = getExecuteWorkflowNodeMutationOptions(optionsWithMeta);

  return useMutation(mutationOptions);
};
